import React, { useState, useEffect } from "react";
import { X, ArrowRight, Sparkles, ShieldCheck, Bug, Bell } from "lucide-react";
import book from "../../../Assets/svg/book.svg";
import carrow from "../../../Assets/svg/carrow.svg";
import colon from "../../../Assets/svg/colon.svg";
import warrow from "../../../Assets/svg/warrow.svg";
import "./V1MigrationModal.css";

const CREATED_BARS = [
  { num: 7, date: "13 Oct", h: 38 },
  { num: 4, date: "12 Oct", h: 22 },
  { num: 7, date: "09 Oct", h: 38 },
  { num: 5, date: "08 Oct", h: 28 },
  { num: 6, date: "07 Oct", h: 33 },
  { num: 6, date: "06 Oct", h: 33 },
  { num: 16, date: "05 Oct", h: 72, isTall: true },
  { num: 5, date: "03 Oct", h: 28 },
  { num: 6, date: "01 Oct", h: 33 },
  { num: 4, date: "30 Sep", h: 22 },
  { num: 5, date: "29 Sep", h: 28 },
  { num: 7, date: "28 Sep", h: 38 },
];

const TICKET_PREVIEWS = [
  {
    code: "M261013049",
    title: "CRM — Customer Outstanding Re...",
    project: "Mawalog",
    prio: "LOW",
    status: "Closed",
    color: "#F44336",
  },
  {
    code: "M261013048",
    title: "Dev, UAT & Live — Environment-S...",
    project: "Mawalog",
    prio: "LOW",
    status: "Open",
    color: "#9E9E9E",
  },
  {
    code: "M261013047",
    title: "Gate Application — New Databas...",
    project: "Mawalog",
    prio: "LOW",
    status: "In Progress",
    color: "#4CAF50",
  },
  {
    code: "M261013046",
    title: "CRM Notifications — Web and Em...",
    project: "Mawalog",
    prio: "LOW",
    status: "Open",
    color: "#9E9E9E",
  },
  {
    code: "M261013045",
    title: "Mail Queue Enhancement — Attac...",
    project: "Mawalog",
    prio: "LOW",
    status: "Open",
    color: "#9E9E9E",
  },
];

const WORKLOAD_PREVIEWS = [
  {
    name: "Narenthiran K",
    role: "Software Trainee",
    avatar: "NK",
    weeks: [
      { text: "40h free", type: "free" },
      { text: "40h free", type: "free" },
      { text: "08:30h", type: "work" },
      { text: "40h free", type: "free" },
    ],
  },
  {
    name: "Pawan C",
    role: "Software Dev",
    avatar: "PC",
    weeks: [
      { text: "29:30h", type: "mixed" },
      { text: "29:20h", type: "work" },
      { text: "34:55h", type: "mixed" },
      { text: "25:30h", type: "work" },
    ],
  },
  {
    name: "Prasanna K",
    role: "Software Trainee",
    avatar: "PK",
    weeks: [
      { text: "52:35h", type: "over" },
      { text: "29:30h", type: "work" },
      { text: "26:20h", type: "work" },
      { text: "40:45h", type: "over" },
    ],
  },
  {
    name: "UI/UX Designer",
    role: "Product Design",
    avatar: "UI",
    weeks: [
      { text: "41:20h", type: "over" },
      { text: "19:55h", type: "work" },
      { text: "37:45h", type: "mixed" },
      { text: "32:30h", type: "work" },
    ],
  },
];

const V1MigrationModal = () => {
  const isV1 =
    typeof window !== "undefined" &&
    window.location.hostname === "tickgo.v1.mawalog.com";
  const [isOpen, setIsOpen] = useState(() => {
    if (isV1) return false;
    if (typeof window === "undefined") return false;
    return !sessionStorage.getItem("tickgo_v1_session_shown");
  });
  const [currentAnim, setCurrentAnim] = useState(1);

  useEffect(() => {
    if (!isV1 && isOpen) {
      sessionStorage.setItem("tickgo_v1_session_shown", "true");
    }
  }, [isV1, isOpen]);

  useEffect(() => {
    const handleOpen = () => {
      setIsOpen(true);
    };
    window.addEventListener("open-v1-modal", handleOpen);
    return () => {
      window.removeEventListener("open-v1-modal", handleOpen);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const timer = setInterval(() => {
      setCurrentAnim((prev) => (prev % 3) + 1);
    }, 4500);
    return () => clearInterval(timer);
  }, [isOpen]);

  if (isV1 || !isOpen) return null;

  const handleStayHere = () => {
    sessionStorage.setItem("tickgo_v1_session_shown", "true");
    setIsOpen(false);
  };

  const handleMoveToV1 = () => {
    sessionStorage.setItem("tickgo_v1_session_shown", "true");
    const v1BaseUrl = process.env.REACT_APP_TICKGO_V1_URL || "https://tickgo.v1.mawalog.com";
    const path = window.location.pathname + window.location.search;
    const targetUrl = `${v1BaseUrl.replace(/\/$/, "")}${path}`;
    window.location.href = targetUrl;
  };

  return (
    <div className="v1-modal-overlay" onClick={handleStayHere}>
      <div className="v1-modal-card" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="v1-modal-close-btn"
          onClick={handleStayHere}
          title="Close"
        >
          <X size={16} />
        </button>

        <div className="v1-modal-header">
          <div className="v1-dashboard-tag">
            <span className="v1-pulse-beacon"></span>
            <span>Tickgo V1.0 Upgrade</span>
          </div>
          <h3 className="v1-modal-title">Switch to Tickgo V1.0</h3>
          <p className="v1-modal-desc">
            Faster workflows, live ticket tracking & instant analytics.
          </p>
        </div>

        {/* Compact 2-Animation Stage */}
        <div className="v1-compact-stage">
          {/* ANIMATION 1: DASHBOARD PREVIEW */}
          <div
            className={`v1-anim-pane ${currentAnim === 1 ? "pane-visible" : "pane-hidden"}`}
          >
            <div className="v1-preview-card">
              {/* Header metrics */}
              <div className="v1-dash-mini-top">
                <div className="v1-dash-metric-main">
                  <span className="v1-dash-big-num">1926</span>
                  <span className="v1-dash-lbl">Total Tickets</span>
                </div>
                <div className="v1-dash-mini-badges">
                  <div className="v1-mini-box">
                    <img src={book} alt="" className="v1-mini-icon" />
                    <span>Open <b>237</b></span>
                  </div>
                  <div className="v1-mini-box">
                    <img src={book} alt="" className="v1-mini-icon" />
                    <span>Hold <b>46</b></span>
                  </div>
                </div>
              </div>

              {/* Status pills row */}
              <div className="v1-status-mini-row">
                <div className="v1-status-pill purple">
                  <span className="v1-stat-title">Inprogress</span>
                  <span className="v1-stat-num">1</span>
                  <img src={carrow} alt="" />
                </div>
                <div className="v1-status-pill blue">
                  <span className="v1-stat-title">Closed</span>
                  <span className="v1-stat-num">1624</span>
                  <img src={carrow} alt="" />
                </div>
                <div className="v1-status-pill green">
                  <span className="v1-stat-title">Completed</span>
                  <span className="v1-stat-num">18</span>
                  <img src={carrow} alt="" />
                </div>
              </div>

              {/* Created Date Bar Chart Animation */}
              <div className="v1-chart-mini-card">
                <div className="v1-chart-mini-head">
                  <div className="v1-chart-head-left">
                    <span>Created date based count</span>
                    <img src={colon} alt="" className="v1-mini-colon" />
                    <span className="v1-chart-highlight">128</span>
                  </div>
                  <span className="v1-date-tag">13 Sep - 13 Oct 2026 ✕</span>
                </div>

                <div className="v1-chart-mini-bars">
                  {CREATED_BARS.map((bar, idx) => (
                    <div key={idx} className="v1-bar-col">
                      <span className="v1-bar-num">{bar.num}</span>
                      <div className="v1-bar-track">
                        <div
                          className={`v1-bar-fill ${bar.isTall ? "tall" : ""}`}
                          style={{
                            height: `${bar.h}px`,
                            animationDelay: `${idx * 0.035}s`,
                          }}
                        ></div>
                      </div>
                      <span className="v1-bar-date">{bar.date}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ANIMATION 2: TICKET LIST PREVIEW */}
          <div
            className={`v1-anim-pane ${currentAnim === 2 ? "pane-visible" : "pane-hidden"}`}
          >
            <div className="v1-preview-card">
              {/* Active Ticket Banner */}
              <div className="v1-tl-mini-banner">
                <div className="v1-tl-banner-indicator"></div>
                <div className="v1-tl-banner-info">
                  <span className="v1-tl-banner-title">
                    Gate Application — New Database Setup, Issue Fixes...
                  </span>
                  <span className="v1-tl-banner-time">
                    Estimation : <b style={{ color: "#6929e2" }}>02:30:00 hrs</b>
                  </span>
                </div>
                <div className="v1-tl-banner-live">
                  <span className="v1-live-pulse-dot"></span>
                  <span>03:48:58 hrs</span>
                </div>
              </div>

              {/* Tabs strip */}
              <div className="v1-tl-tabs-mini">
                <span className="v1-mini-tab active">Assigned (1926)</span>
                <span className="v1-mini-tab">My Tickets (18)</span>
                <span className="v1-mini-tab">Team (3377)</span>
              </div>

              {/* Tickets Table List */}
              <div className="v1-tl-list-mini">
                {TICKET_PREVIEWS.map((ticket, idx) => (
                  <div key={idx} className="v1-tl-item">
                    <span className="v1-tl-code">{ticket.code}</span>
                    <span className="v1-tl-title" title={ticket.title}>
                      {ticket.title}
                    </span>
                    <span className="v1-tl-prio">{ticket.prio}</span>
                    <span className="v1-tl-status">
                      <span
                        className="v1-dot"
                        style={{ backgroundColor: ticket.color }}
                      ></span>
                      {ticket.status}
                    </span>
                  </div>
                ))}
              </div>

              {/* Footer info */}
              <div className="v1-tl-mini-footer">
                <span>Records: 10 per page</span>
                <span>Powered by Mawalog</span>
                <span>Page 1 of 192</span>
              </div>
            </div>
          </div>

          {/* ANIMATION 3: TEAM WORKLOAD PREVIEW */}
          <div
            className={`v1-anim-pane ${currentAnim === 3 ? "pane-visible" : "pane-hidden"}`}
          >
            <div className="v1-preview-card">
              {/* Workload Header */}
              <div className="v1-wl-mini-top">
                <div className="v1-wl-title-wrap">
                  <span className="v1-wl-title">Team Workload</span>
                  <div className="v1-wl-pills">
                    <span className="v1-wl-pill active">Monthly</span>
                    <span className="v1-wl-pill">Weekly</span>
                  </div>
                </div>
                <div className="v1-wl-month">
                  <span>‹ September 2026 ›</span>
                </div>
              </div>

              {/* Workload Table */}
              <div className="v1-wl-table">
                <div className="v1-wl-th">
                  <span className="v1-wl-col-member">Members (40)</span>
                  <span className="v1-wl-col-week">Week 1</span>
                  <span className="v1-wl-col-week">Week 2</span>
                  <span className="v1-wl-col-week">Week 3</span>
                  <span className="v1-wl-col-week">Week 4</span>
                </div>

                <div className="v1-wl-rows">
                  {WORKLOAD_PREVIEWS.map((member, idx) => (
                    <div key={idx} className="v1-wl-row">
                      <div className="v1-wl-member-cell">
                        <span className="v1-wl-avatar">{member.avatar}</span>
                        <div className="v1-wl-name-col">
                          <span className="v1-wl-name">{member.name}</span>
                          <span className="v1-wl-role">{member.role}</span>
                        </div>
                      </div>
                      {member.weeks.map((w, widx) => (
                        <div key={widx} className="v1-wl-cell">
                          <div className={`v1-wl-block ${w.type}`}>
                            <span className="v1-wl-txt">{w.text}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              {/* Workload Footer */}
              <div className="v1-wl-mini-footer">
                <span className="v1-wl-legend">
                  <span className="legend-dot green"></span> Work
                  <span className="legend-dot red"></span> Over
                  <span className="legend-dot grey"></span> Free
                </span>
                <span>Real-time Workload Matrix</span>
              </div>
            </div>
          </div>
        </div>

        {/* Upgrade Highlights Points */}
        <div className="v1-highlights-grid">
          <div className="v1-highlight-chip">
            <Sparkles size={13} className="v1-hl-icon purple" />
            <span>Enhancements & Updates</span>
          </div>
          <div className="v1-highlight-chip">
            <ShieldCheck size={13} className="v1-hl-icon blue" />
            <span>Enhanced Securities</span>
          </div>
          <div className="v1-highlight-chip">
            <Bug size={13} className="v1-hl-icon green" />
            <span>Critical Bug Fixes</span>
          </div>
          <div className="v1-highlight-chip">
            <Bell size={13} className="v1-hl-icon amber" />
            <span>Instant Notifications</span>
          </div>
        </div>

        {/* Modal Bottom Actions */}
        <div className="v1-modal-actions">
          <button
            type="button"
            className="v1-btn-stay"
            onClick={handleStayHere}
          >
            Stay Here
          </button>
          <button
            type="button"
            className="v1-btn-move"
            onClick={handleMoveToV1}
          >
            Move to V1.0
            <ArrowRight size={15} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default V1MigrationModal;



